import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  ApiError,
  getDetections,
  getRules,
  type DetectionEventResponse,
} from "@/api";
import { SeverityBadge } from "@/components/SeverityBadge";
import {
  DEFAULT_FILTERS,
  FilterBar,
  type DetectionFilters,
} from "@/components/FilterBar";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DEFAULT_TIME_RANGE, resolveTimeRange } from "@/lib/timeRange";

// Polling cadence per the dashboard spec. Worst-case detection-to-display
// latency is THRESHOLD_SECONDS + POLLER_INTERVAL_SECONDS + this poll
// interval — about 90 seconds for the slowest rule.
const POLL_INTERVAL_MS = 30_000;

export function DetectionList(): JSX.Element {
  const [filters, setFilters] = useState<DetectionFilters>(() =>
    DEFAULT_FILTERS(DEFAULT_TIME_RANGE),
  );
  // Cursor stack for keyset pagination: each "Next" pushes the response's
  // next_cursor; "Previous" pops back. An empty stack means "first page."
  // Cleared when filters change so we always reset to page 1.
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const handleFiltersChange = (next: DetectionFilters) => {
    setFilters(next);
    setCursorStack([]);
  };

  const cursor = cursorStack[cursorStack.length - 1];

  // Rules query — used for both the FilterBar dropdown and the table's
  // display-name lookup. Static for the session: never refetched.
  const rulesQuery = useQuery({
    queryKey: ["rules"],
    queryFn: getRules,
    staleTime: Infinity,
    refetchInterval: false,
  });

  const ruleDisplayNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const rule of rulesQuery.data?.rules ?? []) {
      map.set(rule.rule_id, rule.display_name);
    }
    return map;
  }, [rulesQuery.data]);

  // Detections query — polled every 30s. queryKey includes the resolved
  // filters so changing severity/rule/time-range triggers a fresh fetch.
  // Preset time ranges keep their key stable across polls; the queryFn
  // recomputes since/until at fetch time so each poll uses fresh "now."
  const detectionsQuery = useQuery({
    queryKey: ["detections", filterKey(filters), cursor],
    queryFn: () => {
      const { since, until } = resolveTimeRange(filters.timeRange);
      return getDetections({
        severities: filters.severities,
        rule_id: filters.ruleId ?? undefined,
        since,
        until,
        cursor,
      });
    },
    refetchInterval: POLL_INTERVAL_MS,
    placeholderData: keepPreviousData,
  });

  const events = detectionsQuery.data?.events ?? [];
  const nextCursor = detectionsQuery.data?.next_cursor ?? null;

  return (
    <main className="container space-y-6 py-8">
      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Detection events
          </h1>
          <p className="text-sm text-muted-foreground">
            Polling every 30s. Worst-case visible latency is approximately
            90s after a real-world failure.
          </p>
        </div>
        {detectionsQuery.isFetching && !detectionsQuery.isLoading && (
          <span className="text-xs text-muted-foreground">Refreshing…</span>
        )}
      </header>

      <FilterBar
        filters={filters}
        rules={rulesQuery.data?.rules ?? []}
        onChange={handleFiltersChange}
      />

      {detectionsQuery.error && (
        <ErrorBanner error={detectionsQuery.error} />
      )}

      <DetectionTable
        events={events}
        ruleDisplayNames={ruleDisplayNames}
        isLoading={detectionsQuery.isLoading}
      />

      <Pagination
        canPrev={cursorStack.length > 0}
        canNext={nextCursor !== null}
        onPrev={() => setCursorStack((s) => s.slice(0, -1))}
        onNext={() => {
          if (nextCursor !== null) {
            setCursorStack((s) => [...s, nextCursor]);
          }
        }}
      />
    </main>
  );
}

/**
 * Convert filters into a stable JSON-serialisable key. Severity order is
 * normalised so toggling in different orders still hits the cache.
 */
function filterKey(filters: DetectionFilters): string {
  return JSON.stringify({
    severities: [...filters.severities].sort(),
    ruleId: filters.ruleId,
    timeRange:
      filters.timeRange.kind === "preset"
        ? { kind: "preset", preset: filters.timeRange.preset }
        : {
            kind: "custom",
            since: filters.timeRange.since.toISOString(),
            until: filters.timeRange.until.toISOString(),
          },
  });
}

function DetectionTable({
  events,
  ruleDisplayNames,
  isLoading,
}: {
  events: DetectionEventResponse[];
  ruleDisplayNames: Map<string, string>;
  isLoading: boolean;
}): JSX.Element {
  if (isLoading) {
    return (
      <div className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (events.length === 0) {
    return (
      <div className="space-y-2 rounded-lg border bg-card p-8 text-center">
        <p className="text-sm text-muted-foreground">
          No detection events match your filters in the selected time range.
        </p>
        <p className="text-xs text-muted-foreground">
          The platform's worst-case end-to-end latency is approximately 90
          seconds (detection threshold + poll interval). Very recent events
          may not yet have been captured.
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-lg border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[110px]">Severity</TableHead>
            <TableHead>Rule</TableHead>
            <TableHead className="w-[170px]">Detected</TableHead>
            <TableHead className="w-[130px]">Inquiry</TableHead>
            <TableHead>Message</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {events.map((event) => (
            <DetectionRow
              key={event.id}
              event={event}
              displayName={
                ruleDisplayNames.get(event.rule_id) ?? event.rule_id
              }
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function DetectionRow({
  event,
  displayName,
}: {
  event: DetectionEventResponse;
  displayName: string;
}): JSX.Element {
  const detectedAt = new Date(event.detected_at);
  const relative = formatDistanceToNow(detectedAt, { addSuffix: true });
  const absolute = detectedAt.toISOString();

  return (
    <TableRow>
      <TableCell>
        <SeverityBadge severity={event.severity} />
      </TableCell>
      <TableCell>
        <div className="font-medium">{displayName}</div>
        <div className="text-xs text-muted-foreground">{event.rule_id}</div>
      </TableCell>
      <TableCell>
        <span title={absolute}>{relative}</span>
      </TableCell>
      <TableCell>
        {event.inquiry_id ? (
          <Link
            to={`/inquiry/${event.inquiry_id}`}
            className="font-mono text-xs text-primary underline-offset-4 hover:underline"
            title={String(event.inquiry_id)}
          >
            {event.inquiry_id}
          </Link>
        ) : (
          <span className="rounded-md border border-input bg-muted px-2 py-0.5 text-xs text-muted-foreground">
            (meta)
          </span>
        )}
      </TableCell>
      <TableCell>
        <div className="line-clamp-1 max-w-[640px]" title={event.message}>
          {event.message}
        </div>
      </TableCell>
    </TableRow>
  );
}

function Pagination({
  canPrev,
  canNext,
  onPrev,
  onNext,
}: {
  canPrev: boolean;
  canNext: boolean;
  onPrev: () => void;
  onNext: () => void;
}): JSX.Element {
  return (
    <div className="flex items-center justify-end gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={onPrev}
        disabled={!canPrev}
      >
        Previous
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={onNext}
        disabled={!canNext}
      >
        Next
      </Button>
    </div>
  );
}

function ErrorBanner({ error }: { error: unknown }): JSX.Element {
  const message =
    error instanceof ApiError
      ? `Request failed: ${error.status} ${error.message}`
      : error instanceof Error
        ? error.message
        : "Unknown error";
  return (
    <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-2 text-sm text-destructive">
      {message}
    </div>
  );
}
